import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { useSelectionCountContext } from 'core/states';
import { IInfiniteQueryOptions } from 'core/types';
import { projectUserService } from 'projects/services';
import { IProjectUser } from 'projects/types';

export const useGetProjectUsers = (
  requestPayload?: { project: number },
  options: IInfiniteQueryOptions<IProjectUser> = {
    fetchAllPages: true,
  }
) =>
  useInfiniteQuery<IProjectUser>(
    projectUserService.getProjectUsers,
    'projectUsers',
    requestPayload,
    options
  );

export const useProjectUserActions = (
  notificationPosition: NotificationPosition = NotificationPosition.FORM
) => {
  const { selectionCount } = useSelectionCountContext();

  return {
    ...useUpsertProjectUser(notificationPosition),
    ...useRemoveProjectUsers(selectionCount, notificationPosition),
  };
};

const useUpsertProjectUser = (notificationPosition: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: ({ projectCorporateUser }) =>
      projectCorporateUser?.id
        ? { message: t('messageProjectUserUpdateSuccess') }
        : { message: t('messageProjectUserSaveSuccess') },
    loading: ({ projectCorporateUser }) =>
      projectCorporateUser?.corporateUserId
        ? { message: t('messageProjectUserUpdateInProgress') }
        : { message: t('messageProjectUserSaveInProgress') },
    error: ({ projectCorporateUser }) =>
      projectCorporateUser?.corporateUserId
        ? { title: t('errorTitleProjectUserUpdate') }
        : { title: t('errorTitleProjectUserSave') },
    invalidateQueries: ['projectUsers'],
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation((data) => projectUserService.upsertProjectUser(data), {
    ...errors,
  });

  return { upsertProjectUser: mutateAsync };
};

const useRemoveProjectUsers = (
  selectionCount: number,
  notificationPosition: NotificationPosition
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageProjectUsersRemoveSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageProjectUsersRemoveInProgress', { count: selectionCount }),
    },
    error: {
      title: t('errorTitleProjectUsersRemove', { count: selectionCount }),
    },
    invalidateQueries: ['projectUsers'],
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation((data) => projectUserService.removeProjectUsers(data), {
    ...errors,
  });

  return { removeProjectUsers: mutateAsync };
};
